import { useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ErrorManagementService from '@/services/error-management';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDropScrollEvent } from '@/hooks/use-drop-scroll-event';
import { useUserRoles } from '@/hooks/use-user-roles';

const PAGE_SIZE = 10;

export type BulletinWithProfile = {
  id: string;
  content: string;
  image_url?: string | null;
  thumbnail_url?: string | null;
  file_type?: string | null;
  image_version?: number | null;
  created_at: string;
  user_id: string;
  is_update: boolean;
  like_count: number; // Read from loops table
  comment_count?: number; // Read from loops table
  view_count?: number; // Read from loops table
  loop_id?: string | null;
  upload_progress?: number;
  moderation_status: string;
  expires_at?: string | null;
  is_relic?: boolean;
  saved_at?: string | null;
  profiles: {
    username: string;
    profile_image_url?: string | null;
    is_admin: boolean;
    is_premium: boolean;
    combo_score: number;
    is_partner?: boolean;
  };
};

// Fetch function for relics (randomized order)
const fetchRelicsPage = async (pageParam: number) => {
  const startIndex = pageParam * PAGE_SIZE;
  
  try {
    const { data, error } = await supabase
      .from('bulletins')
      .select(`
        *,
        profiles:profiles(username, profile_image_url, is_admin, is_premium, is_partner, aura_points)
      `)
      .eq('is_relic', true)
      .in('moderation_status', ['approved', 'flagged'])
      .order('created_at', { ascending: false })
      .range(startIndex, startIndex + PAGE_SIZE - 1);

    if (error) {
      ErrorManagementService.handleQueryError(error);
      throw error;
    }

    return {
      items: data || [],
      nextPage: data?.length === PAGE_SIZE ? pageParam + 1 : undefined
    };
  } catch (error) {
    ErrorManagementService.handleQueryError(error);
    throw error;
  }
};

const fetchBulletinsPage = async (pageParam: number, activeTab: string, category?: string) => {
  const startIndex = pageParam * PAGE_SIZE;
  const currentTime = new Date().toISOString();
  
  console.log('🔍 [BULLETIN QUERY] Starting query:', {
    activeTab,
    category,
    pageParam,
    startIndex,
    timestamp: new Date().toISOString()
  });
  
  try {
    // Join with loops to get engagement data (loops is master)
    let query = supabase
      .from('bulletins')
      .select(`
        *,
        loops!bulletins_loop_id_fkey(
          like_count,
          comment_count,
          view_count,
          reblog_count
        )
      `)
      .order('created_at', { ascending: false })
      // Show all posts now (we no longer promote relics separately)
      // Allow approved/flagged/pending and legacy rows with NULL moderation_status
      .or('moderation_status.in.(approved,flagged,pending),moderation_status.is.null' as any);

    // Artstream filter: only show bulletins marked for Artstream
    query = query.eq('is_artstream', true);

    // For the updates tab require true; for pour-toi include false or null
    if (activeTab === 'updates') {
      console.log('📝 [BULLETIN QUERY] Filtering for updates only');
      query = query.eq('is_update', true);
    } else {
      console.log('📝 [BULLETIN QUERY] Filtering for non-updates');
      query = query.or('is_update.eq.false,is_update.is.null' as any);
    }

    if (category && category !== 'all' && category !== 'chrono') {
      console.log('📂 [BULLETIN QUERY] Filtering by category:', category);
      query = query.or(`auto_category.eq.${category},user_category.eq.${category}`);
    }

    // Note: Do not apply any 24h time window here. Only the Top Follows feed
    // (`use-top-follows-feed.ts`) should filter to the last 24 hours.
    // This feed should return the full archive (subject to category/moderation filters).

    const { data, error } = await query.range(startIndex, startIndex + PAGE_SIZE - 1);

    console.log('✅ [BULLETIN QUERY] Raw query result:', {
      dataCount: data?.length || 0,
      error: error?.message,
      sampleIds: data?.slice(0, 3).map(b => b.id) || []
    });

    if (error) {
      ErrorManagementService.handleQueryError(error);
      throw error;
    }

    // Robustly fetch profiles in a second query (avoid FK-based joins)
    const raw = (data || []) as any[];
    const userIds = Array.from(new Set(raw.map((r) => r.user_id).filter(Boolean)));
    let profilesMap = new Map<string, any>();
    
    if (userIds.length) {
      console.log('[BULLETIN PROFILES] Fetching profiles for user IDs:', userIds);
      
      // Try profiles table first (main source of truth)
      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('id, username, profile_image_url, is_admin, is_premium, is_partner, aura_points')
        .in('id', userIds);
      
      if (pErr) {
        console.error('[BULLETIN PROFILES] ERROR fetching profiles:', pErr);
        ErrorManagementService.handleQueryError(pErr);
      } else if (profiles) {
        console.log('[BULLETIN PROFILES] Successfully fetched', profiles.length, 'profiles');
        for (const p of profiles as any[]) {
          profilesMap.set(p.id, p);
        }
        
        // Log any missing profiles
        const missingIds = userIds.filter(id => !profilesMap.has(id));
        if (missingIds.length > 0) {
          console.warn('[BULLETIN PROFILES] Missing profiles for user IDs:', missingIds);
        }
      } else {
        console.warn('[BULLETIN PROFILES] No profiles returned from query');
      }
    }

    const itemsWithProfiles = raw.map((r) => {
      const profile = profilesMap.get(r.user_id);
      if (!profile) {
        console.error('[BULLETIN PROFILES] No profile found for bulletin:', {
          bulletinId: r.id,
          userId: r.user_id,
          availableProfileIds: Array.from(profilesMap.keys())
        });
      }
      
      // Get engagement from loops (master) or fall back to bulletin values
      const loop = (r as any).loops;
      const like_count = loop?.like_count ?? r.like_count ?? 0;
      const comment_count = loop?.comment_count ?? 0;
      const view_count = loop?.view_count ?? 0;
      
      return {
        ...r,
        like_count, // From loops
        comment_count, // From loops
        view_count, // From loops
        profiles: profile || {
          username: 'unknown',
          profile_image_url: null,
          is_admin: false,
          is_premium: false,
          is_partner: false,
          aura_points: 0,
        },
      };
    });

    console.log('🎯 [BULLETIN TRANSFORM] Final transformed data:', {
      originalCount: raw.length,
      transformedCount: itemsWithProfiles.length,
      hasProfiles: itemsWithProfiles.filter(b => b.profiles.username !== 'unknown').length,
      sampleBulletin: itemsWithProfiles[0] ? {
        id: itemsWithProfiles[0].id,
        content: itemsWithProfiles[0].content?.substring(0, 50),
        username: itemsWithProfiles[0].profiles.username
      } : null
    });

    return {
      items: itemsWithProfiles,
      nextPage: data?.length === PAGE_SIZE ? pageParam + 1 : undefined
    };
  } catch (error) {
    ErrorManagementService.handleQueryError(error);
    throw error;
  }
};

export const useInfiniteBulletins = (activeTab: 'pour-toi' | 'updates', category?: string) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { processScroll } = useDropScrollEvent();
  const dropsEnabled = (import.meta as any)?.env?.VITE_DROPS_ENABLED === 'true';
  const { isAdmin, isPartner, loading: rolesLoading } = useUserRoles();
  
  // Force clear cache to fix post-relic-disable issues
  useEffect(() => {
    console.log('🔄 [BULLETIN CACHE] Force clearing cache for:', { activeTab, category });
    queryClient.invalidateQueries({
      queryKey: ['infinite-bulletins'],
      exact: false
    });
  }, [queryClient]);
  
  // Increased cache times to reduce race conditions
  const staleTime = 1000 * 60 * 2; // 2 minutes to match global config
  const gcTime = isMobile ? 1000 * 60 * 10 : 1000 * 60 * 10; // 10 min for all devices

  const queryResult = useInfiniteQuery({
      queryKey: ['infinite-bulletins', activeTab, category || 'all'],
      queryFn: ({ pageParam = 0 }) => {
        console.log('🔍 [BULLETIN FETCH] Fetching page:', { pageParam, activeTab, category });
        return fetchBulletinsPage(pageParam, activeTab, category);
      },
      getNextPageParam: (lastPage) => {
        console.log('📄 [BULLETIN PAGES] Next page param:', lastPage.nextPage, 'Items in page:', lastPage.items?.length);
        return lastPage.nextPage;
      },
      initialPageParam: 0,
      staleTime: staleTime,
      gcTime: gcTime,
      // Always enable refetch on mount, regardless of device
      refetchOnMount: true,
      // Enable refetch on window focus for all devices to help with realtime updates
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // Implement exponential backoff for retries
        const shouldRetry = failureCount < 3 && !ErrorManagementService.shouldReportError(error);
        if (shouldRetry) {
          const delay = ErrorManagementService.calculateBackoffDelay(failureCount);
          console.log(`Retrying query after ${delay}ms (attempt ${failureCount + 1})`);
        }
        return shouldRetry;
      },
      meta: {
        errorContext: 'Fetching bulletins failed',
      }
    });

  // Debug logging for data transformation
  useEffect(() => {
    const data = queryResult.data;
    const items = data?.pages.flatMap(page => page.items) ?? [];
    console.log('📊 [BULLETIN DATA] Query result:', {
      hasData: !!data,
      pagesCount: data?.pages?.length || 0,
      totalItems: items.length,
      isLoading: queryResult.isLoading,
      isError: queryResult.isError,
      error: queryResult.error?.message,
      firstPageItems: data?.pages?.[0]?.items?.length || 0
    });
  }, [queryResult.data, queryResult.isLoading, queryResult.isError, queryResult.error]);

  useEffect(() => {
    if (!dropsEnabled) return;
    if (activeTab !== 'pour-toi') return;
    if (rolesLoading) return;
    if (!(isAdmin || isPartner)) return;
    if (queryResult.isLoading || queryResult.isFetching) return;
    void processScroll();
  }, [dropsEnabled, activeTab, rolesLoading, isAdmin, isPartner, queryResult.isLoading, queryResult.isFetching, queryResult.data]);

  return {
    ...queryResult,
    addOptimisticBulletin: (newBulletin: BulletinWithProfile) => {
      console.log('➕ [BULLETIN CACHE] Adding optimistic bulletin:', {
        id: newBulletin.id,
        content: newBulletin.content?.substring(0, 50) + '...',
        has_image: !!newBulletin.image_url,
        expires_at: newBulletin.expires_at,
        timestamp: new Date().toISOString()
      });
      
      queryClient.setQueryData(
        ['infinite-bulletins', activeTab, category || 'all'],
        (oldData: any) => {
          if (!oldData?.pages?.length) {
            console.log('⚠️ [BULLETIN CACHE] No existing pages, returning oldData');
            return oldData;
          }
          
          // Check if bulletin already exists to prevent duplicates
          const existingBulletin = oldData.pages.flatMap((page: any) => page.items)
            .find((item: any) => item.id === newBulletin.id);
            
          if (existingBulletin) {
            console.log('⚠️ [BULLETIN CACHE] Bulletin already exists in cache, skipping optimistic update');
            return oldData;
          }
          
          const updatedData = {
            ...oldData,
            pages: [{
              ...oldData.pages[0],
              items: [newBulletin, ...oldData.pages[0].items]
            }, ...oldData.pages.slice(1)]
          };
          
          console.log('✅ [BULLETIN CACHE] Added bulletin to cache, new total count:', 
            updatedData.pages.flatMap((p: any) => p.items).length);
          
          return updatedData;
        }
      );
    },
    updateOptimisticBulletin: (bulletinId: string, updates: Partial<BulletinWithProfile>) => {
      console.log('🔄 [BULLETIN CACHE] Updating optimistic bulletin:', {
        id: bulletinId,
        updates: Object.keys(updates),
        timestamp: new Date().toISOString()
      });
      
      queryClient.setQueryData(
        ['infinite-bulletins', activeTab, category || 'all'],
        (oldData: any) => {
          if (!oldData?.pages?.length) {
            console.log('⚠️ [BULLETIN CACHE] No pages for update');
            return oldData;
          }
          
          const updatedPages = oldData.pages.map((page: any) => ({
            ...page,
            items: page.items.map((item: BulletinWithProfile) => 
              item.id === bulletinId ? { ...item, ...updates } : item
            )
          }));
          
          // Check if any bulletins were actually updated
          const bulletinExists = updatedPages.some((page: any) => 
            page.items.some((item: any) => item.id === bulletinId)
          );
          
          // If no bulletins were updated, the ID might not exist yet
          if (!bulletinExists) {
            console.warn(`⚠️ [BULLETIN CACHE] Bulletin with ID ${bulletinId} not found for update`);
          } else {
            console.log('✅ [BULLETIN CACHE] Bulletin updated successfully');
          }
          
          return {
            ...oldData,
            pages: updatedPages
          };
        }
      );
    },
    removeOptimisticBulletin: (bulletinId: string) => {
      console.log('➖ [BULLETIN CACHE] Removing optimistic bulletin:', {
        id: bulletinId,
        timestamp: new Date().toISOString()
      });
      
      queryClient.setQueryData(
        ['infinite-bulletins', activeTab, category || 'all'],
        (oldData: any) => {
          if (!oldData?.pages?.length) {
            console.log('⚠️ [BULLETIN CACHE] No pages for removal');
            return oldData;
          }
          
          const updatedData = {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              items: page.items.filter((item: any) => item.id !== bulletinId)
            }))
          };
          
          console.log('✅ [BULLETIN CACHE] Bulletin removed, new total count:', 
            updatedData.pages.flatMap((p: any) => p.items).length);
          
          return updatedData;
        }
      );
    },
    fetchBulletinById: async (bulletinId: string) => {
      try {
        const { data, error } = await supabase
          .from('bulletins')
          .select(`
            *,
            profiles:profiles(username, profile_image_url, is_admin, is_premium, aura_points)
          `)
          .eq('id', bulletinId)
          .maybeSingle();

        if (error) {
          ErrorManagementService.handleQueryError(error);
          throw error;
        }

        // Update the cache with the fresh bulletin data or remove if missing
        if (data) {
          queryClient.setQueryData(
            ['infinite-bulletins', activeTab, category || 'all'],
            (oldData: any) => {
              if (!oldData?.pages?.length) return oldData;
              const updatedPages = oldData.pages.map((page: any) => ({
                ...page,
                items: page.items.map((item: BulletinWithProfile) => 
                  item.id === bulletinId ? { ...item, ...data } : item
                )
              }));
              return { ...oldData, pages: updatedPages };
            }
          );
        } else {
          // If the bulletin no longer exists (expired/deleted), remove it from cache
          queryClient.setQueryData(
            ['infinite-bulletins', activeTab, category || 'all'],
            (oldData: any) => {
              if (!oldData?.pages?.length) return oldData;
              const updatedPages = oldData.pages.map((page: any) => ({
                ...page,
                items: page.items.filter((item: BulletinWithProfile) => item.id !== bulletinId)
              }));
              return { ...oldData, pages: updatedPages };
            }
          );
        }

        return data;
      } catch (error) {
        ErrorManagementService.handleQueryError(error);
        throw error;
      }
    }
  };
};
